import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, LinearProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader, SearchFilterBar, DataTable, StatusChip } from '../components';
import { useCounts } from '../hooks/useInventoryCounts';
import { colors, formatDate } from '../constants';

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export default function InventoryCounts() {
  const history = useHistory();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { data, isLoading } = useCounts(
    statusFilter ? { status: statusFilter, page: 1, limit: 100 } : { page: 1, limit: 100 }
  );

  const rows = useMemo(() => {
    const list = data?.data ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (c) =>
        c.uuid.toLowerCase().includes(q) ||
        c.type?.toLowerCase().includes(q) ||
        c.status?.toLowerCase().includes(q) ||
        c.countedBy?.toLowerCase().includes(q) ||
        c.notes?.toLowerCase().includes(q)
    );
  }, [data, search]);

  const columns = [
    {
      key: 'uuid',
      label: 'Count',
      render: (r) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {r.uuid.slice(0, 8)} — {r.type?.toUpperCase()}
          </Typography>
          <Typography variant="caption" sx={{ color: colors.gray500 }}>
            {r.createdAt ? formatDate(r.createdAt) : '—'}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (r) => {
        const total = r.itemCount ?? 0;
        const counted = r.countedItemCount ?? 0;
        const pct = total > 0 ? Math.round((counted / total) * 100) : 0;
        return (
          <Box sx={{ minWidth: 140 }}>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
            />
            <Typography variant="caption" sx={{ color: colors.gray500 }}>
              {counted}/{total}
            </Typography>
          </Box>
        );
      },
    },
    { key: 'status', label: 'Status', render: (r) => <StatusChip status={r.status} /> },
  ];

  return (
    <>
      <PageHeader
        title="Inventory Counts"
        actionLabel="New Count"
        actionIcon={<AddIcon />}
        onAction={() => history.push('/inventory-management/inventory-counts/new')}
      />
      <SearchFilterBar
        searchPlaceholder="Search counts..."
        search={search}
        onSearchChange={setSearch}
        filters={[
          { label: 'Status', value: statusFilter, onChange: setStatusFilter, options: STATUS_OPTIONS },
        ]}
      />
      <DataTable
        columns={columns}
        rows={rows}
        loading={isLoading}
        emptyMessage="No counts found"
        onRowClick={(r) => history.push(`/inventory-management/inventory-counts/${r.uuid}`)}
      />
    </>
  );
}
